import { NavLink, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '@clerk/clerk-react';
import { useAuthStore } from '@/stores/authStore';
import { useUnreadCount } from '@/hooks/useMessages';
import { useAuthReady } from '@/hooks/useAuthReady';
import {
  LayoutDashboard,
  Building2,
  DoorOpen,
  CalendarDays,
  Calendar,
  MessageSquare,
  Settings,
  Users,
  BarChart3,
  X,
  CreditCard,
  Compass,
  UsersRound,
  Shield,
  Crown,
  Wrench,
  UserPlus,
  Megaphone,
  MessagesSquare,
  LifeBuoy,
  StarIcon,
  Mail,
  Activity,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { useTrashCount } from '@/hooks/useSoftDelete';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  key: string;
  icon: LucideIcon;
  path: string;
  color: string;       // text color for icon
  bgColor: string;     // bg color for icon wrapper
  activeBg: string;    // active gradient icon bg
}

const navItems: NavItem[] = [
  { key: 'dashboard', icon: LayoutDashboard, path: '/dashboard', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-50 dark:bg-blue-500/10', activeBg: 'bg-white/25' },
  { key: 'properties', icon: Building2, path: '/dashboard/properties', color: 'text-violet-600 dark:text-violet-400', bgColor: 'bg-violet-50 dark:bg-violet-500/10', activeBg: 'bg-white/25' },
  { key: 'units', icon: DoorOpen, path: '/dashboard/units', color: 'text-cyan-600 dark:text-cyan-400', bgColor: 'bg-cyan-50 dark:bg-cyan-500/10', activeBg: 'bg-white/25' },
  { key: 'bookings', icon: CalendarDays, path: '/dashboard/bookings', color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-50 dark:bg-emerald-500/10', activeBg: 'bg-white/25' },
  { key: 'calendar', icon: Calendar, path: '/dashboard/calendar', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-50 dark:bg-orange-500/10', activeBg: 'bg-white/25' },
  { key: 'messages', icon: MessageSquare, path: '/dashboard/messages', color: 'text-pink-600 dark:text-pink-400', bgColor: 'bg-pink-50 dark:bg-pink-500/10', activeBg: 'bg-white/25' },
  { key: 'smartInbox', icon: Mail, path: '/dashboard/smart-inbox', color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-50 dark:bg-indigo-500/10', activeBg: 'bg-white/25' },
  { key: 'localGuide', icon: Compass, path: '/dashboard/local-guide', color: 'text-teal-600 dark:text-teal-400', bgColor: 'bg-teal-50 dark:bg-teal-500/10', activeBg: 'bg-white/25' },
  { key: 'hostUpdates', icon: Megaphone, path: '/dashboard/host-updates', color: 'text-rose-600 dark:text-rose-400', bgColor: 'bg-rose-50 dark:bg-rose-500/10', activeBg: 'bg-white/25' },
  { key: 'community', icon: MessagesSquare, path: '/dashboard/community', color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-50 dark:bg-indigo-500/10', activeBg: 'bg-white/25' },
  { key: 'reviews', icon: StarIcon, path: '/dashboard/reviews', color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-50 dark:bg-amber-500/10', activeBg: 'bg-white/25' },
  { key: 'team', icon: UsersRound, path: '/dashboard/team', color: 'text-sky-600 dark:text-sky-400', bgColor: 'bg-sky-50 dark:bg-sky-500/10', activeBg: 'bg-white/25' },
  { key: 'billing', icon: CreditCard, path: '/dashboard/billing', color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-50 dark:bg-amber-500/10', activeBg: 'bg-white/25' },
  { key: 'support', icon: LifeBuoy, path: '/dashboard/support', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-50 dark:bg-orange-500/10', activeBg: 'bg-white/25' },
  { key: 'trash', icon: Trash2, path: '/dashboard/trash', color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-50 dark:bg-amber-500/10', activeBg: 'bg-white/25' },
  { key: 'settings', icon: Settings, path: '/dashboard/settings', color: 'text-gray-600 dark:text-gray-400', bgColor: 'bg-gray-100 dark:bg-gray-500/10', activeBg: 'bg-white/25' },
];

const adminItems: { key: string; icon: LucideIcon; path: string; color: string; bgColor: string }[] = [
  { key: 'adminDashboard', icon: LayoutDashboard, path: '/dashboard/admin', color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-50 dark:bg-amber-500/10' },
  { key: 'users', icon: Users, path: '/dashboard/admin/users', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-50 dark:bg-orange-500/10' },
  { key: 'inviteLandlord', icon: UserPlus, path: '/dashboard/admin/invite', color: 'text-yellow-600 dark:text-yellow-400', bgColor: 'bg-yellow-50 dark:bg-yellow-500/10' },
  { key: 'subscribers', icon: Crown, path: '/dashboard/admin/subscribers', color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-50 dark:bg-amber-500/10' },
  { key: 'analytics', icon: BarChart3, path: '/dashboard/admin/analytics', color: 'text-rose-600 dark:text-rose-400', bgColor: 'bg-rose-50 dark:bg-rose-500/10' },
  { key: 'adminSupport', icon: LifeBuoy, path: '/dashboard/admin/support', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-50 dark:bg-orange-500/10' },
  { key: 'appSettings', icon: Wrench, path: '/dashboard/admin/settings', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-50 dark:bg-orange-500/10' },
  { key: 'systemHealth', icon: Activity, path: '/dashboard/admin/system-health', color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-50 dark:bg-emerald-500/10' },
];

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const { user } = useUser();
  const { userId } = useAuthReady();
  const { isAdmin } = useAuthStore();
  const { data: unreadCount = 0 } = useUnreadCount(userId);
  const { data: trashCount = 0 } = useTrashCount();
  const location = useLocation();

  // Determine which nav item should be active based on the current URL
  // Unit edit/detail pages are nested under /dashboard/properties/:id/units/...
  // but should highlight "Smještajne jedinice" instead of "Objekti"
  const getIsActive = (itemKey: string, routerIsActive: boolean): boolean => {
    const path = location.pathname;
    if (itemKey === 'units' && /\/dashboard\/properties\/[^/]+\/units\//.test(path)) {
      return true;
    }
    if (itemKey === 'properties' && /\/dashboard\/properties\/[^/]+\/units\//.test(path)) {
      return false;
    }
    return routerIsActive;
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 transform border-r border-[hsl(var(--border))]
          bg-[hsl(var(--background))] transition-all duration-300 ease-out
          md:static md:translate-x-0
          ${isOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}
        `}
      >
        {/* Mobile header */}
        <div className="flex h-16 items-center justify-between px-4 md:hidden border-b border-[hsl(var(--border))]">
          <Link to="/dashboard" className="flex items-center" onClick={onClose}>
            <img src="/favicon-original.png" alt="BookiApp" className="h-8 w-auto" />
          </Link>
          <Button variant="ghost" size="icon" onClick={onClose} className="hover:bg-[hsl(var(--muted))]">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Desktop logo */}
        <div className="hidden md:flex h-14 items-center px-5 border-b border-[hsl(var(--border))]">
          <Link to="/dashboard">
            <img src="/favicon-original.png" alt="BookiApp" className="h-8 w-auto cursor-pointer" />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex flex-col gap-0.5 p-3 overflow-y-auto h-[calc(100vh-3.5rem)]">
          <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
            Menu
          </p>

          {navItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.key}
                to={item.path}
                end={item.path === '/dashboard' || item.path === '/dashboard/properties'}
                onClick={onClose}
                data-onboarding={`nav-${item.key}`}
                className={({ isActive: routerActive }) => {
                  const isActive = getIsActive(item.key, routerActive);
                  return `group flex items-center gap-3 rounded-xl px-2.5 py-2 transition-all duration-200 animate-fade-in ${
                    isActive
                      ? 'bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--primary)/0.8)] text-white shadow-lg shadow-[hsl(var(--primary)/0.3)]'
                      : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]'
                  }`;
                }}
                style={{ animationDelay: `${index * 0.03}s` }}
              >
                {({ isActive: routerActive }) => {
                  const isActive = getIsActive(item.key, routerActive);
                  return (
                  <>
                    <div className={`flex h-8 w-8 items-center justify-center rounded-lg transition-all duration-200 ${
                      isActive ? item.activeBg : item.bgColor
                    }`}>
                      <Icon className={`h-[18px] w-[18px] transition-colors duration-200 ${isActive ? 'text-white' : item.color}`} strokeWidth={2} />
                    </div>
                    <span className="flex-1 text-sm font-semibold">{t(`nav.${item.key}`)}</span>
                    {item.key === 'messages' && unreadCount > 0 && (
                      <span
                        aria-live="polite"
                        aria-label={`${unreadCount} nepročitanih poruka`}
                        className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-gradient-to-r from-rose-500 to-pink-500 px-1.5 text-xs font-bold text-white shadow-lg shadow-rose-500/30"
                      >
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                    {item.key === 'trash' && trashCount > 0 && (
                      <span
                        aria-live="polite"
                        aria-label={`${trashCount} u smeću`}
                        className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-1.5 text-xs font-bold text-white shadow-lg shadow-amber-500/30"
                      >
                        {trashCount > 99 ? '99+' : trashCount}
                      </span>
                    )}
                  </>
                  );
                }}
              </NavLink>
            );
          })}

          {/* Admin section */}
          {isAdmin() && (
            <>
              <div className="my-4 border-t border-[hsl(var(--border))]" />
              <div className="mb-2 flex items-center gap-2 px-3">
                <Shield className="h-3.5 w-3.5 text-amber-500" />
                <p className="text-xs font-semibold uppercase tracking-wider text-amber-500">
                  {t('nav.admin')}
                </p>
              </div>
              {adminItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.key}
                    to={item.path}
                    end={item.path === '/dashboard/admin'}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 rounded-xl px-2.5 py-2 transition-all duration-200 ${
                        isActive
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/25'
                          : 'text-[hsl(var(--muted-foreground))] hover:bg-amber-500/10 hover:text-amber-600 dark:hover:text-amber-400'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <div className={`flex h-8 w-8 items-center justify-center rounded-lg transition-all duration-200 ${
                          isActive ? 'bg-white/25' : item.bgColor
                        }`}>
                          <Icon className={`h-[18px] w-[18px] transition-colors duration-200 ${isActive ? 'text-white' : item.color}`} strokeWidth={2} />
                        </div>
                        <span className="font-semibold text-sm">{t(`nav.${item.key}`)}</span>
                      </>
                    )}
                  </NavLink>
                );
              })}
            </>
          )}

          {/* User section at bottom */}
          {user && (
            <div className="mt-auto pt-4 border-t border-[hsl(var(--border))]">
              <div className="flex items-center gap-3 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10 px-3 py-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-bold shadow-lg shadow-blue-500/25">
                  {user.firstName?.[0] || user.emailAddresses[0]?.emailAddress?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate text-[hsl(var(--foreground))]">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">
                    {user.emailAddresses[0]?.emailAddress}
                  </p>
                </div>
              </div>
            </div>
          )}
        </nav>
      </aside>
    </>
  );
}

export default Sidebar;
